<template>
  <div class="p-[8px]">
    <template v-if="formations.length">
      <refonte-table
        v-bind:on_details="goToDetails"
        key="formations-1"
        table_index="formations-1"
        v-bind:columns="columns"
        v-bind:data="formations">
        <template v-slot:itemTags="{ rowIndex }">
          <refonte-tag v-bind:icon="IconFlagParcours">
            <refonte-number variant="count">{{ rowIndex }}</refonte-number>
          </refonte-tag>
        </template>
      </refonte-table>
    </template>
    <refonte-search-results-empty v-else />
    <formations-elearning-retractation-modal
      v-if="selectedRetractationFormation"
      v-bind:show="showRetractationModal"
      v-bind:on_close="closeRetractationModal"
      v-bind:formation="mapRetractationFormation(selectedRetractationFormation)"
      v-bind:open_elearning_module="openElearningModule" />
  </div>
</template>

<script setup lang="ts">
  import IconFlagParcours from '@/assets/img/refonte/icons/information/icon-flag-parcours.svg?component';
  import RefonteTag from '@/components/refonte/tags/refonte-tag.vue';
  import RefonteNumber from '@/components/refonte/numbers/refonte-number.vue';
  import RefonteTable from '@/components/refonte/tables/refonte-table.vue';
  import { storeToRefs } from 'pinia';
  import RefonteSearchResultsEmpty from '@/components/refonte/inputs/refonte-search-results-empty.vue';
  import { h, ref } from 'vue';
  import { useFormationStore } from '@/stores/participant/formations';
  import {
    Formation,
    RetractationFormation
  } from '@/types/participant/formations-types';
  import FormationsTableItemDocuments from '@/views/_espace/_refonte/participant/_formations/formations-table-item-documents.vue';
  import FormationsTableItemActions from '@/views/_espace/_refonte/participant/_formations/formations-table-item-actions.vue';
  import { useRouter } from 'vue-router';
  import FormationsElearningRetractationModal from '@/views/_espace/_refonte/participant/_formations/formations-elearning-retractation-modal.vue';
  import FormationsTableItemDateDeFormation from '@/views/_espace/_refonte/participant/_formations/formations-table-item-date-de-formation.vue';
  import { API } from '@/http-common';

  const props = withDefaults(
    defineProps<{
      fetch?: Function;
    }>(),
    {}
  );

  const showRetractationModal = ref<boolean>(false);
  const selectedRetractationFormation = ref<Formation>();
  const openRetractationModal = (formation: Formation) => {
    selectedRetractationFormation.value = formation;
    showRetractationModal.value = true;
  };

  const closeRetractationModal = () => {
    selectedRetractationFormation.value = undefined;
    showRetractationModal.value = false;
  };
  const columns = [
    {
      content_index: 'reference',
      label: 'Référence',
      min_width: 116,
      width: 120
    },
    {
      content_index: 'titre',
      tags_type: 'typeShortcut',
      tags_type_label: 'typeLabel',
      content_sub_text: (row: Formation) => `${row.lieu ?? ''}`,
      tag_text: (row: Formation) => row.produit?.titre,
      tag_type_label: (row: Formation) => row.produit?.type.label,
      tag_type_id: (row: Formation) => row.produit?.type.id,
      min_width: 240,
      width: 360
    },
    {
      content_index: 'datesFormations',
      label: 'Dates de formation',
      content_custom: (row: Formation) =>
        h(FormationsTableItemDateDeFormation, {
          row
        }),
      min_width: 190,
      width: 290
    },
    {
      content_index: 'labelStatut',
      label: 'Inscription',
      width: 85,
      content_custom: (row: Formation) =>
        h('span', {
          innerHTML: row.labelStatut,
          class:
            row.labelStatut === 'Annulé' || row.labelStatut === 'Transféré'
              ? 'text-jinius-red'
              : ''
        })
    },
    {
      content_index: 'organisateur',
      label: 'Organisateur',
      min_width: 120,
      width: 140
    },
    {
      content_index: 'action',
      label: 'Actions à réaliser',
      min_width: 200,
      width: 250,
      content_custom: (row: Formation) => {
        if (row.disabled) {
          return h('span', '-');
        }
        return h(FormationsTableItemActions, {
          row,
          refresh_rows: props.fetch ? props.fetch : () => {},
          open_elearning_retractation_modal: openRetractationModal,
          open_elearning_module: openElearningModule
        });
      },
      class: () => '!flex-grow-0'
    },
    {
      content_index: 'id',
      label: 'Documents',
      min_width: 150,
      width: 200,
      content_custom: (row: Formation) => {
        if (row.disabled) {
          return h('span', '-');
        }
        return h(FormationsTableItemDocuments, {
          row,
          refresh_rows: props.fetch ? props.fetch : () => {}
        });
      },
      class: (row: Formation) => {
        if (row.attestationId || row.pifStatut) {
          return '!bg-refonte-pale-violet-2 !flex-grow-0 ';
        }
        return '!flex-grow-0';
      }
    }
  ];

  const formationStore = useFormationStore();

  const { formations } = storeToRefs(formationStore);
  const router = useRouter();
  const goToDetails = (row: Formation) => {
    if (row?.sessionParticipantId) {
      if (row?.isSousProduitParcours === true) {
        router.push(
          `/inscription/formations-details?id=${row.sessionParticipantId}&produitId=${row.produitId}`
        );
      } else {
        router.push(
          `/inscription/formations-details?id=${row.sessionParticipantId}`
        );
      }
    } else if (row?.elearningParticipantId) {
      router.push(
        `/inscription/formations-details-elearning?id=${row.elearningParticipantId}`
      );
    } else if (row?.cursusInscriptionId) {
      router.push(
        `/inscription/formations-cursus-details?id=${row.cursusInscriptionId}`
      );
    }
  };
  const openElearningModule = async (formation: Formation) => {
    let elearningModuleUrl;
    if (formation.elearningParticipantId) {
      elearningModuleUrl = `/participant/v2/formations/elearning/${formation.elearningParticipantId}/elearning`;
    } else if (formation.produit && formation.produit.type.id == 4) {
      elearningModuleUrl = `/participant/v2/formations/session/${formation.sessionParticipantId}/parcours/${formation.produitId}/elearning`;
    } else if (formation.sessionParticipantId) {
      elearningModuleUrl = `/participant/v2/formations/session/${formation.sessionParticipantId}/elearning`;
    }
    if (elearningModuleUrl) {
      const response = await API.get(elearningModuleUrl, {
        headers: { 'SKIP-REDIRECTION': true }
      });

      if (response && response.data && response.data.url) {
        closeRetractationModal();
        window.open(response.data.url);
        if (props.fetch) {
          props.fetch();
        }
      }
    }
  };
  const mapRetractationFormation = (
    formation: Formation
  ): RetractationFormation => ({
    organisateurEmail: formation.organisateurEmail,
    moduleElearning: formation.moduleElearning,
    elearningParticipantId: formation.elearningParticipantId,
    sessionParticipantId: formation.sessionParticipantId
  });
</script>

<style lang="scss" scoped></style>
